import React, { useCallback, useEffect, useMemo } from "react";

import { useRouterWithTypedParams } from "../../../../hooks/useRouterWithTypedParams";
import { PageHeader } from "../../../PageHeader";
import { ConfigurationContainer } from "../../../shared/ConfigurationContainer";
import { LogoUploadSection } from "../../../shared/LogoUploadSection";
import { useApi } from "../../../useApi";

import { DealerEditForm } from "./DealerEditForm";
import { useDealerLoad } from "./useDealerLoad";
import { useDealerLogoLoad } from "./useDealerLogoLoad";

export const DealerConfigPage = () => {
    const { match } = useRouterWithTypedParams<{ dealerId?: number }>();
    const dealerId = match.params.dealerId;
    const { dealerApi } = useApi();

    const {
        loadDealer,
        loading: isLoadingDealer,
        dealer,
    } = useDealerLoad(dealerId);

    const {
        loadLogo,
        loading: isLoadingLogo,
        logo,
    } = useDealerLogoLoad(dealerId);

    const loadDealerData = useCallback(() => {
        loadDealer();
        loadLogo();
    }, [loadDealer, loadLogo]);

    const uploadLogoMethod = useMemo(() => {
        if (!dealerId) return;
        return (data: FormData) => dealerApi.postLogo(dealerId, data);
    }, [dealerApi, dealerId]);

    const deleteLogoMethod = useMemo(() => {
        if (!dealerId) return;
        return () => dealerApi.deleteLogo(dealerId);
    }, [dealerApi, dealerId]);

    useEffect(() => {
        loadDealerData();
    }, [loadDealerData]);

    const loadingData = isLoadingDealer || isLoadingLogo;

    return (
        <>
            <PageHeader isLoading={loadingData}>
                Dealer Configuration: {dealer && dealer.name}
            </PageHeader>

            {dealer && (
                <ConfigurationContainer>
                    <DealerEditForm
                        dealer={dealer}
                        onEditSuccess={loadDealer}
                        disabled={loadingData}
                    />

                    {uploadLogoMethod && deleteLogoMethod && (
                        <LogoUploadSection
                            label="Logo"
                            onUploadSuccess={loadLogo}
                            disabled={loadingData}
                            loadedLogo={logo}
                            loadingLogo={isLoadingLogo}
                            uploadMethod={uploadLogoMethod}
                            deleteMethod={deleteLogoMethod}
                        />
                    )}
                </ConfigurationContainer>
            )}
        </>
    );
};
